import { SyncOutlined } from '@ant-design/icons';
import { Payment } from '@interfaces/payment';
import {
    ISmsAutoRechargeSettings,
    ISmsAutoRechargeUpdate,
    ISmsCreditPackage,
    SMS_AUTO_RECHARGE_CAP_PRESETS,
    SMS_AUTO_RECHARGE_THRESHOLDS,
} from '@interfaces/sms-credits';
import { HttpError, useApiUrl, useCustom, useCustomMutation } from '@refinedev/core';
import Response from '@responses/response';
import { convertPrice } from '@utils/resource';
import { getSmsCreditsErrorCode } from '@utils/sms-credits-error';
import { Alert, Button, Card, Col, notification, Row, Select, Switch } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SMS_CAP_CUSTOM as CUSTOM, SmsAutoRechargeCapField } from './sms-auto-recharge-cap-field';
import { SmsAutoRechargeStatusAlerts } from './sms-auto-recharge-status-alerts';
import { SmsCreditsSectionTitle } from './sms-credits-section-title';

type Props = { packages: ISmsCreditPackage[]; defaultCard: Payment.IPaymentMethod | null };

export const SmsAutoRechargeForm: React.FC<Props> = ({ packages, defaultCard }) => {
    const { t, i18n } = useTranslation(['marketplace']);
    const apiUrl = useApiUrl();
    const url = `${apiUrl}/v1/sms-credits/auto-recharge`;

    const { data, isLoading, refetch } = useCustom<Response<ISmsAutoRechargeSettings>>({
        url,
        method: 'get',
        errorNotification: false,
    });
    const { mutate, isLoading: isSaving } = useCustomMutation<
        Response<ISmsAutoRechargeSettings>,
        HttpError
    >();
    const settings = data?.data?.data;

    const [enabled, setEnabled] = useState(false);
    const [threshold, setThreshold] = useState<number | undefined>();
    const [packageId, setPackageId] = useState<string | undefined>();
    const [capChoice, setCapChoice] = useState<number | typeof CUSTOM | undefined>();
    const [customCapUsd, setCustomCapUsd] = useState<number | null>(null);

    // Hydrate the local form from the server settings.
    useEffect(() => {
        if (!settings) return;
        setEnabled(settings.enabled);
        setThreshold(settings.threshold ?? undefined);
        setPackageId(settings.packageId ?? undefined);
        const cap = settings.monthlyCap;
        if (cap == null) {
            setCapChoice(undefined);
            setCustomCapUsd(null);
        } else if ((SMS_AUTO_RECHARGE_CAP_PRESETS as readonly number[]).includes(cap)) {
            setCapChoice(cap);
        } else {
            setCapChoice(CUSTOM);
            setCustomCapUsd(cap / 100);
        }
    }, [settings]);

    const adminMax = settings?.adminMaxCap ?? null;
    const locked = !!settings?.adminDisabled;
    const capCents =
        capChoice === CUSTOM
            ? customCapUsd != null
                ? Math.round(customCapUsd * 100)
                : undefined
            : capChoice;
    const pkgPrice = packages.find((p) => p.id === packageId)?.price;
    const capError =
        enabled && capCents != null
            ? adminMax != null && capCents > adminMax
                ? t('smsCredits.errors.cap_above_admin_max', { max: convertPrice(adminMax) })
                : pkgPrice != null && capCents < pkgPrice
                ? t('smsCredits.errors.cap_below_package_price')
                : null
            : null;
    const selectedPkg = packages.find((p) => p.id === packageId);
    // The saved package was deactivated by Nuvioo (`packages` lists active ones only): never render its raw id.
    const packageUnavailable = !!packageId && packages.length > 0 && !selectedPkg;
    const belowThreshold =
        enabled && threshold != null && selectedPkg != null && selectedPkg.credits < threshold;
    const incomplete = enabled && (!threshold || !packageId || packageUnavailable || !capCents);

    const handleSave = () => {
        const values: ISmsAutoRechargeUpdate = enabled
            ? { enabled, threshold, packageId, monthlyCap: capCents }
            : { enabled: false };
        mutate(
            { url, method: 'put', values, errorNotification: false },
            {
                onSuccess: () => {
                    notification.success({
                        className: 'success-notification',
                        message: '',
                        description: t('smsCredits.autoRecharge.saved'),
                    });
                    refetch();
                },
                onError: (error) => {
                    const code = getSmsCreditsErrorCode(error);
                    notification.error({
                        className: 'error-notification',
                        message: '',
                        description: t(
                            `smsCredits.errors.${
                                code && code !== 'payment_failed' ? code : 'unknown'
                            }`,
                            { max: adminMax != null ? convertPrice(adminMax) : '' },
                        ),
                    });
                },
            },
        );
    };

    const fieldsDisabled = locked || !enabled;
    // Save only when something would change (an "off" form that is already off has nothing to save).
    const dirty =
        !settings ||
        enabled !== settings.enabled ||
        (enabled &&
            (threshold !== (settings.threshold ?? undefined) ||
                packageId !== (settings.packageId ?? undefined) ||
                capCents !== (settings.monthlyCap ?? undefined)));

    return (
        <Card
            loading={isLoading}
            title={
                <SmsCreditsSectionTitle icon={<SyncOutlined />}>
                    {t('smsCredits.autoRecharge.title')}
                </SmsCreditsSectionTitle>
            }
        >
            <p className="sms-ar-description">{t('smsCredits.autoRecharge.description')}</p>
            <SmsAutoRechargeStatusAlerts
                settings={settings}
                locked={locked}
                enabled={enabled}
                adminMax={adminMax}
                packageUnavailable={packageUnavailable}
                hasDefaultCard={!!defaultCard}
            />

            <label className="sms-ar-switch">
                <Switch
                    checked={enabled}
                    disabled={locked || (!defaultCard && !enabled)}
                    onChange={setEnabled}
                    aria-label={t('smsCredits.autoRecharge.enable')}
                />
                {t('smsCredits.autoRecharge.enable')}
            </label>

            {/* Three steps of one rule, side by side on desktop: when -> what -> how much at most. */}
            <Row gutter={[16, 16]} className={`sms-ar-fields${fieldsDisabled ? ' is-off' : ''}`}>
                <Col xs={24} md={8}>
                    <label className="sms-ar-label" id="sms-ar-threshold">
                        {t('smsCredits.autoRecharge.threshold')}
                    </label>
                    <Select
                        aria-labelledby="sms-ar-threshold"
                        style={{ width: '100%' }}
                        disabled={fieldsDisabled}
                        value={threshold}
                        onChange={(v: number) => {
                            setThreshold(v);
                            // Drop a package that is now smaller than the threshold.
                            const sel = packages.find((p) => p.id === packageId);
                            if (sel && sel.credits < v) setPackageId(undefined);
                        }}
                        placeholder={t('smsCredits.autoRecharge.select')}
                        options={SMS_AUTO_RECHARGE_THRESHOLDS.map((v) => ({
                            value: v,
                            label: t('smsCredits.autoRecharge.thresholdOption', {
                                credits: v.toLocaleString(i18n.language),
                            }),
                        }))}
                    />
                </Col>
                <Col xs={24} md={8}>
                    <label className="sms-ar-label" id="sms-ar-package">
                        {t('smsCredits.autoRecharge.package')}
                    </label>
                    <Select
                        aria-labelledby="sms-ar-package"
                        aria-describedby={threshold != null ? 'sms-ar-package-hint' : undefined}
                        style={{ width: '100%' }}
                        disabled={fieldsDisabled}
                        value={packageUnavailable ? undefined : packageId}
                        onChange={setPackageId}
                        placeholder={t('smsCredits.autoRecharge.select')}
                        options={packages.map((p) => ({
                            value: p.id,
                            // A package smaller than the threshold would re-trigger every sweep.
                            disabled: threshold != null && p.credits < threshold,
                            label: `${p.credits.toLocaleString(i18n.language)} - ${convertPrice(
                                p.price,
                            )}`,
                        }))}
                    />
                    {threshold != null && (
                        <small id="sms-ar-package-hint" className="sms-ar-hint">
                            {t('smsCredits.autoRecharge.packageMinHint', {
                                credits: threshold.toLocaleString(i18n.language),
                            })}
                        </small>
                    )}
                </Col>
                <Col xs={24} md={8}>
                    <SmsAutoRechargeCapField
                        disabled={fieldsDisabled}
                        adminMax={adminMax}
                        choice={capChoice}
                        onChoice={setCapChoice}
                        customUsd={customCapUsd}
                        onCustomUsd={setCustomCapUsd}
                        error={capError}
                    />
                </Col>
            </Row>

            {enabled &&
                !incomplete &&
                !belowThreshold &&
                selectedPkg &&
                capCents != null &&
                !capError && (
                    <Alert
                        className="sms-ar-summary"
                        type="success"
                        showIcon
                        aria-live="polite"
                        message={t('smsCredits.autoRecharge.summary', {
                            threshold: (threshold ?? 0).toLocaleString(i18n.language),
                            credits: selectedPkg.credits.toLocaleString(i18n.language),
                            price: convertPrice(selectedPkg.price),
                            cap: convertPrice(capCents),
                        })}
                    />
                )}

            <div className="sms-ar-footer">
                <Button
                    type="primary"
                    loading={isSaving}
                    disabled={locked || !dirty || incomplete || !!capError || belowThreshold}
                    onClick={handleSave}
                >
                    {t('smsCredits.autoRecharge.save')}
                </Button>
                {settings?.payerUserId && settings.enabled && (
                    <small className="sms-ar-hint">{t('smsCredits.autoRecharge.payer')}</small>
                )}
            </div>
        </Card>
    );
};
